import { Box, Button, Typography, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { PopupBody, PopupFooter } from "vig-ui-library";
import { MagicLinkContactPicker } from "./MagicLinkContactPicker";
import { RequestMagicLinkMethod } from "../Enums/RequestMagicLinkMethod";

export type ResendMagicLinkModalProps = {
    onClose: () => void;
    magicLinkEmails: string[];
    magicLinkPhones: string[];
    initialRecipient: string | null;
    initialDeliveryMethod: RequestMagicLinkMethod | null;
    onCreateLink: (recipient: string, method: RequestMagicLinkMethod) => Promise<void>;
    isCreating: boolean;
};

export const ResendMagicLinkModal = ({
    onClose,
    magicLinkEmails,
    magicLinkPhones,
    initialRecipient,
    initialDeliveryMethod,
    onCreateLink,
    isCreating,
}: ResendMagicLinkModalProps) => {
    const theme = useTheme();

    // Local state for modal - only update global state after successful send
    const [localRecipient, setLocalRecipient] = useState<string | null>(initialRecipient);
    const [localDeliveryMethod, setLocalDeliveryMethod] = useState<RequestMagicLinkMethod | null>(
        initialDeliveryMethod ?? RequestMagicLinkMethod.Sms
    );
    // Local loading state so the spinner shows immediately on click; modal content may not re-render when parent task state updates
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLoading = isCreating || isSubmitting;

    const handleCreateLink = async () => {
        if (!localRecipient || localDeliveryMethod === null) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onCreateLink(localRecipient, localDeliveryMethod);
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <PopupBody>
                <Box sx={{ padding: 2 }}>
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: 2,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Odoslať Magic Link znovu
                    </Typography>
                    <MagicLinkContactPicker
                        magicLinkEmails={magicLinkEmails}
                        magicLinkPhones={magicLinkPhones}
                        magicLinkRecipient={localRecipient}
                        setMagicLinkRecipient={setLocalRecipient}
                        magicLinkDeliveryMethod={localDeliveryMethod}
                        setMagicLinkDeliveryMethod={setLocalDeliveryMethod}
                    />
                </Box>
            </PopupBody>
            <PopupFooter
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    padding: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{
                        textTransform: "none",
                    }}
                >
                    Zatvoriť
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={handleCreateLink}
                    disabled={!localRecipient || localDeliveryMethod === null || localDeliveryMethod === undefined || isLoading}
                    loading={isLoading}
                    sx={{
                        textTransform: "none",
                    }}
                >
                    Odoslať link
                </LoadingButton>
            </PopupFooter>
        </>
    );
};

